/**
 * OneVeracity Design Tokens
 *
 * Official brand colors from OneVeracity Brand Guidelines
 * Custom theme tokens that extend Fluent UI's design system
 * with Veracity brand colors while maintaining accessibility.
 */

import {
  createLightTheme,
  createDarkTheme,
  BrandVariants,
  Theme,
} from '@fluentui/react-components';

// =============================================================================
// OneVeracity Official Brand Colors
// =============================================================================

/**
 * OneVeracity Official Color Palette
 * Source: OneVeracity Brand Guidelines
 */
export const veracityColors = {
  // Primary
  veracityOrange: '#D6461D',

  // Secondary Colors
  daybreakAmber: '#F79E31',
  morningCoral: '#EE6352',
  horizonBlue: '#A4C0C1',
  twilightPlum: '#553746',
  duskBlue: '#3F4C5C',
  clarityGreen: '#597B4B',

  // Neutral Colors
  midnightCharcoal: '#1C1919',
  gentleHaze: '#FFE6C8',
  firstLight: '#FFF1E0',
} as const;

/**
 * Brand variants generated from Veracity Orange #D6461D.
 *
 * DIRECTION MATTERS, and it is the opposite of what reads naturally: Fluent's
 * `BrandVariants` scale runs 10 = DARKEST to 160 = LIGHTEST (their own ramp
 * opens at #061724 and closes at #ebf3fc). This table shipped the other way
 * round, so every brand token came out inverted: `colorBrandBackground2`, the
 * pale tint Fluent puts BEHIND dark text, resolved to #4A0D06 near-black brown,
 * and `colorBrandForegroundLink`, meant to be a deep readable shade, resolved to
 * #F57D5D pale salmon. Symptoms were tracked one surface at a time (Sasha 9/15
 * unreadable Analytics selection, 9/20 unreadable RFP "Products to quote" chips)
 * before the shared cause was fixed here on 2026-09-20.
 *
 * If you ever re-derive these shades, sort DARK to LIGHT. The reference points:
 * 80 carries the primary background and the brand foreground, so it holds the
 * true Veracity Orange; 160 is the faintest tint.
 *
 * See docs/design/vera-visual-refresh.md.
 */
const veracityBrand: BrandVariants = {
  10: '#4A0D06',  // Darkest - inverted surfaces
  20: '#5E1509',
  30: '#721D0C',
  40: '#86250F', // Pressed state for primary
  50: '#9A2D12',
  60: '#AE3515',
  70: '#C23D19', // Hover state for primary; links
  80: '#D6461D', // Primary brand color (Veracity Orange)
  90: '#E66742',
  100: '#F57D5D',
  110: '#FF9378',
  120: '#FFA993',
  130: '#FFBFAE',
  140: '#FFD4C9', // Subtle brand strokes
  150: '#FFEAE4',
  160: '#FFF5F2', // Lightest - subtle backgrounds behind dark text
};

// =============================================================================
// Theme Configuration
// =============================================================================

/**
 * Light theme with Veracity branding
 */
export const veracityLightTheme: Theme = {
  ...createLightTheme(veracityBrand),
};

/**
 * Dark theme with Veracity branding
 */
export const veracityDarkTheme: Theme = {
  ...createDarkTheme(veracityBrand),
};

// =============================================================================
// Semantic Color Tokens
// =============================================================================

/**
 * Semantic colors mapped to OneVeracity brand palette
 */
export const semanticColors = {
  // Status colors (OneVeracity secondary palette)
  success: veracityColors.clarityGreen,     // #597B4B - Clarity Green
  warning: veracityColors.daybreakAmber,    // #F79E31 - Daybreak Amber
  error: veracityColors.morningCoral,       // #EE6352 - Morning Coral
  info: veracityColors.duskBlue,            // #3F4C5C - Dusk Blue

  // Brand accents
  accent: veracityColors.veracityOrange,    // #D6461D - Primary brand
  accentSecondary: veracityColors.horizonBlue, // #A4C0C1 - Horizon Blue
  accentTertiary: veracityColors.twilightPlum, // #553746 - Twilight Plum

  // Light theme surfaces (warm neutrals per UX guide)
  surfaceLight: '#FFFFFF',
  surfaceMuted: veracityColors.firstLight,   // #FFF1E0 - page background
  surfaceWarm: veracityColors.gentleHaze,    // #FFE6C8 - hover/selected
  surfaceElevated: '#FFFFFF',
  // Warm off-white for cards sitting ON the cream page ground. Pure white
  // against #FFF1E0 is the flatness the 2026-09 design review reacted to:
  // the card reads as a hole rather than a raised surface.
  surfaceRaised: '#FFFBF4',
  surfaceDark: veracityColors.midnightCharcoal, // #1C1919

  // Text colors (warm-tinted per UX guide)
  textPrimary: veracityColors.midnightCharcoal, // #1C1919
  textSecondary: '#6b5240',                  // Warm medium
  textDisabled: '#a8896b',                   // Warm muted
  textOnDark: veracityColors.firstLight,     // #FFF1E0

  // Border colors (warm-tinted per UX guide)
  borderSubtle: '#f0dcc4',
  borderDefault: '#e8d5bc',
  borderStrong: '#c4a98a',
} as const;

// =============================================================================
// Spacing Tokens
// =============================================================================

/**
 * Spacing scale following 4px base unit
 */
export const spacing = {
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
} as const;

// =============================================================================
// Typography Tokens
// =============================================================================

/**
 * Typography settings - DM Sans (body) + Fraunces (prose) + JetBrains Mono (meta).
 *
 * These faces were declared here from the start, citing a UX_GUIDE_UNIFIED.md
 * that was never committed, and were then never LOADED: the shell had no
 * @font-face and this export had zero importers, so Vera rendered in Segoe UI.
 * The faces are now self-hosted in vera-shell/src/index.css.
 *
 * Fraunces is new to this export but not to the app: the Academy certificate
 * has been using it since Phase 3, and it is the best-looking screen we have.
 * It is the serif role the redesign asked for.
 *
 * See docs/design/vera-visual-refresh.md.
 */
export const typography = {
  fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
  fontFamilyProse: "'Fraunces', Georgia, serif",
  fontFamilyMono: "'JetBrains Mono', ui-monospace, monospace",

  // Font sizes
  fontSizeBase100: '10px',
  fontSizeBase200: '12px',
  fontSizeBase300: '14px',  // Body text
  fontSizeBase400: '16px',
  fontSizeBase500: '20px',
  fontSizeBase600: '24px',  // Headings

  // Line heights
  lineHeightBase100: '14px',
  lineHeightBase200: '16px',
  lineHeightBase300: '20px',
  lineHeightBase400: '22px',
  lineHeightBase500: '28px',
  lineHeightBase600: '32px',

  // Font weights
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemibold: 600,
  fontWeightBold: 700,
} as const;

/**
 * Display tier - EDITORIAL SURFACES ONLY.
 *
 * Fluent's ramp stops at 24px, so every Vera page has been built out of body
 * text and 24px headings. That single ceiling is most of why the app reads
 * flat: hierarchy needs a real jump, not one more weight.
 *
 * Use on page heroes, score numbers, certificates and landing panels. NEVER in
 * a table, a form, a filter bar or a data card. If it sits next to a column
 * header, it does not get a display size.
 */
export const display = {
  fontSizeDisplay100: '32px',  // section heading
  fontSizeDisplay200: '44px',  // page hero
  fontSizeDisplay300: '64px',  // score, certificate, landing
  lineHeight: 1.02,
  letterSpacing: '-0.03em',
  fontWeight: 700,
} as const;

/**
 * Eyebrow - uppercase tracked mono meta ("ONBOARDING . WEEK 1").
 *
 * The cheapest "designed" signal available: no new face, no layout change,
 * and it gives a heading something to sit against. Spread it into a Griffel
 * rule, do not hand a template literal to a Fluent component.
 */
export const eyebrow = {
  fontFamily: typography.fontFamilyMono,
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
} as const;

// =============================================================================
// Shadow Tokens
// =============================================================================

/**
 * Elevation shadows — DEPRECATED per UX guide.
 * Use 1px border instead of shadows on cards and data elements.
 * Kept for Fluent UI compatibility only.
 */
export const shadows = {
  shadow2: 'none',
  shadow4: 'none',
  shadow8: 'none',
  shadow16: 'none',
  shadow28: 'none',
  shadow64: 'none',
} as const;

// =============================================================================
// Border Radius Tokens
// =============================================================================

/**
 * Border radius scale
 */
/**
 * Border radius scale — per UX guide:
 * 8px on data elements (cards, tables), 6px on buttons
 */
export const borderRadius = {
  none: '0px',
  small: '4px',
  medium: '6px',    // Buttons, controls
  large: '8px',     // Cards, data elements
  xLarge: '8px',    // Capped at 8px per UX guide
  /**
   * EDITORIAL SURFACES ONLY: course cards, page heroes, certificates, pull
   * quotes, media panels.
   *
   * The 8px cap above stays exactly as it is. It is why Vera's dense screens
   * read cleanly, and lifting it globally would make an Ops table look like a
   * marketing page. This step exists so long-form content surfaces can soften
   * without dragging the data surfaces with them. A table row, a form field,
   * a filter pill and a data card do not qualify.
   */
  editorial: '18px',
  circular: '9999px',
} as const;

// =============================================================================
// Breakpoints
// =============================================================================

/**
 * Responsive breakpoints
 */
export const breakpoints = {
  xs: '320px',
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1366px',
  xxl: '1920px',
} as const;

/**
 * Media query helpers
 */
export const mediaQueries = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
  xxl: `@media (min-width: ${breakpoints.xxl})`,
} as const;

// =============================================================================
// Animation Tokens
// =============================================================================

/**
 * Animation durations and easings
 */
export const animation = {
  durationUltraFast: '50ms',
  durationFaster: '100ms',
  durationFast: '150ms',
  durationNormal: '200ms',
  durationSlow: '300ms',
  durationSlower: '400ms',
  durationUltraSlow: '500ms',

  easingAccelerate: 'cubic-bezier(0.9, 0.1, 1, 0.2)',
  easingDecelerate: 'cubic-bezier(0.1, 0.9, 0.2, 1)',
  easingLinear: 'cubic-bezier(0, 0, 1, 1)',
  easingMax: 'cubic-bezier(0.8, 0, 0.2, 1)',
} as const;
